import { formatDate } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as ExcelJS from 'exceljs';
import { GenericListDataReadAllPaginatedRequestDTO } from 'src/app/Common/UICServices/ListUIC/list-uic.interface';
import { GenericListUicService } from 'src/app/Common/UICServices/ListUIC/list-uic.service';
import { ExportableGrid, ExportColumn, ExportScope } from './export-excel-button.interface';

/**
 * Generic "Export to Excel" button for any UIC-driven list screen.
 *
 * Design rules it enforces:
 *  1. Columns come from the grid's `visibleColumns` — so the export matches what the user
 *     actually sees, including their Column Settings toggles and drag reorder. Nothing extra.
 *  2. Headers are the UIC `uiLabel`, never the DB column name.
 *  3. Icon/print/action columns carry no data and are excluded.
 *  4. The user picks scope: the current page, or every record behind the current
 *     search + column filters + sort.
 *  5. A preview modal always shows exactly what will be written before anything downloads.
 *
 * Usage:
 *   <app-uic-color-table #poGrid [uicListData]="uicList" [tableData]="itemData"
 *                        [ReadAllDTO]="ReadAllDTO"></app-uic-color-table>
 *
 *   <app-export-excel-button [grid]="poGrid" [ReadAllDTO]="ReadAllDTO"
 *                            fileName="Purchase Order Outstanding"
 *                            sheetName="PO Outstanding"></app-export-excel-button>
 */
@Component({
  selector: 'app-export-excel-button',
  templateUrl: './export-excel-button.component.html',
  styleUrls: ['./export-excel-button.component.css']
})
export class ExportExcelButtonComponent {

  // ===================== INPUTS =====================

  /** The grid to export. Pass the <app-uic-color-table> template reference. */
  @Input() grid!: ExportableGrid;

  /**
   * The host screen's paginated DTO. Used as the fallback source for an "All records"
   * fetch when the grid has no working DTO of its own (i.e. the screen never passed
   * [ReadAllDTO] to the table). When the grid DOES have one, the grid's copy wins because
   * it also carries in-grid column filters and sort.
   */
  @Input() ReadAllDTO?: GenericListDataReadAllPaginatedRequestDTO;

  /** Base file name. A yyyy-MM-dd stamp and .xlsx are appended automatically. */
  @Input() fileName: string = 'Export';

  /** Worksheet tab name. Falls back to fileName. Excel caps this at 31 chars. */
  @Input() sheetName: string = '';

  /** Optional merged title row above the header row. Empty = no title row. */
  @Input() reportTitle: string = '';

  /** Button text. Set showLabel=false for an icon-only button. */
  @Input() buttonLabel: string = 'Export';
  @Input() showLabel: boolean = true;
  @Input() buttonClass: string = 'btn btn-sm btn-success';
  @Input() buttonIcon: string = 'bi bi-file-earmark-excel';
  @Input() buttonTooltip: string = 'Export to Excel';

  /** Disable the button from the host screen (e.g. while the grid is still loading). */
  @Input() disabled: boolean = false;

  /** How many rows the preview table renders. The export itself is never truncated by this. */
  @Input() previewRowLimit: number = 100;

  /** Hard safety ceiling on an "All records" fetch. */
  @Input() maxExportRows: number = 100000;

  /**
   * Include a leading serial-number column. Default false so the export contains
   * nothing that is not a real on-screen column. Turn on for grids using
   * [enableAutoSerial]="true", which do show a serial column.
   */
  @Input() includeSerial: boolean = false;

  /** Header fill colour (ARGB). Matches the preview header so the file looks like the preview. */
  @Input() headerColor: string = 'FF0A58CA';

  // ===================== OUTPUTS =====================

  @Output() exported = new EventEmitter<{ scope: ExportScope; rowCount: number; fileName: string }>();

  // ===================== INTERNALS =====================

  @ViewChild('exportPreviewModal', { static: true }) exportPreviewModal!: TemplateRef<any>;

  private modalService = inject(NgbModal);
  private UICListService = inject(GenericListUicService);
  private modalRef?: NgbModalRef;

  scope: ExportScope = 'page';
  columns: ExportColumn[] = [];

  /** Rows for the chosen scope BEFORE the date filter. */
  private sourceRows: any[] = [];
  /** Rows after the date filter — the exact set that will be written. */
  rows: any[] = [];

  /** Cache of a fetched "all records" set, plus the server predicate it was fetched with. */
  private allRowsCache: any[] | null = null;
  private allRowsCacheKey: string | null = null;

  // ---- date filter state ----
  /** Chosen date column (`ExportColumn.key`); '' when the user has not picked one. */
  dateFilterKey: string = '';
  dateFrom: string = '';
  dateTo: string = '';
  dateFilterError: string = '';

  isFetching: boolean = false;
  isBuilding: boolean = false;
  errorMessage: string = '';

  // ===================== BUTTON STATE =====================

  /**
   * Grid must have rendered its columns before an export means anything.
   * Kept allocation-free: this getter is read from the template on every change-detection
   * pass, so it must not map/rebuild the column array.
   */
  get isReady(): boolean {
    const visible = this.grid?.visibleColumns;
    if (!visible || !visible.length) { return false; }
    return visible.some(col => !!col && !!col.colData && !col.isButton);
  }

  get isDisabled(): boolean {
    return this.disabled || !this.isReady;
  }

  /** Rows on the current page. */
  get pageRowCount(): number {
    return this.grid?.tableData?.length ?? 0;
  }

  /**
   * Total records behind the current search/filters. Every row carries the pagination
   * envelope (rowNum / pageNo / pageSize / totalCount), so row 0 is the source of truth.
   */
  get totalRowCount(): number {
    const total = Number(this.grid?.tableData?.[0]?.totalCount ?? 0);
    return Number.isFinite(total) && total > 0 ? total : this.pageRowCount;
  }

  /** An "All records" refetch needs a DTO to clone. Without one, only the page can be exported. */
  get canExportAll(): boolean {
    return !!(this.grid?.workingReadAllDTO || this.ReadAllDTO);
  }

  /**
   * True only when the fetch actually came back at the ceiling. Checked against the rows
   * received rather than `totalRowCount`, because a server-side date filter can bring the
   * result under the cap even when the unfiltered total is far above it.
   */
  get isCapped(): boolean {
    return this.scope === 'all' && !this.isFetching && this.sourceRows.length >= this.maxExportRows;
  }

  get resolvedRowCount(): number {
    return this.rows.length;
  }

  /** Rows available for the current scope, before the date filter. */
  get sourceRowCount(): number {
    return this.sourceRows.length;
  }

  /** How many rows the date filter removed from the current scope. */
  get dateFilteredOutCount(): number {
    return Math.max(this.sourceRows.length - this.rows.length, 0);
  }

  get previewRows(): any[] {
    return this.rows.slice(0, this.previewRowLimit);
  }

  get isPreviewTruncated(): boolean {
    return this.rows.length > this.previewRowLimit;
  }

  // ===================== OPEN / CLOSE =====================

  openExportModal(): void {
    if (this.isDisabled) { return; }

    this.errorMessage = '';
    this.allRowsCache = null;
    this.allRowsCacheKey = null;
    this.columns = this.resolveColumns();
    this.scope = 'page';

    // Default the date filter to the first date column, with no range applied yet.
    this.dateFilterKey = this.dateColumns[0]?.key ?? '';
    this.dateFrom = '';
    this.dateTo = '';
    this.dateFilterError = '';

    this.sourceRows = [...(this.grid?.tableData ?? [])];
    this.recomputeRows();

    this.modalRef = this.modalService.open(this.exportPreviewModal, {
      size: 'xl',
      scrollable: false,
      ariaLabelledBy: 'exportPreviewTitle'
    });

    this.modalRef.closed.subscribe(() => this.reset());
    this.modalRef.dismissed.subscribe(() => this.reset());
  }

  closeModal(): void {
    this.modalRef?.close();
  }

  private reset(): void {
    this.sourceRows = [];
    this.rows = [];
    this.allRowsCache = null;
    this.allRowsCacheKey = null;
    this.isFetching = false;
    this.isBuilding = false;
    this.errorMessage = '';
    this.dateFilterError = '';
  }

  // ===================== SCOPE =====================

  onScopeChange(scope: ExportScope): void {
    if (this.scope === scope || this.isFetching || this.isBuilding) { return; }
    this.scope = scope;
    this.errorMessage = '';

    if (scope === 'page') {
      this.sourceRows = [...(this.grid?.tableData ?? [])];
      this.recomputeRows();
      return;
    }

    this.loadAllRows();
  }

  /** Uses the cached set when it was fetched with the same server predicate, else refetches. */
  private loadAllRows(): void {
    const key = this.buildDateWhereClause();
    if (this.allRowsCache && this.allRowsCacheKey === key) {
      this.sourceRows = this.allRowsCache;
      this.recomputeRows();
      return;
    }
    this.fetchAllRows(key);
  }

  /**
   * Refetches the full result set. Clones the grid's working DTO so the export honours the
   * host screen's search WHERE clause AND any in-grid column filters and sort, then asks
   * for every row in a single page.
   *
   * `dateClause` is ANDed onto that WHERE clause when the chosen date column has a
   * `whereClauseName`. Filtering in the database matters here rather than only in the
   * browser: `pageSize` is capped at `maxExportRows`, so a client-only filter would be
   * applied to an already-truncated window and could silently return the wrong rows.
   */
  private fetchAllRows(dateClause: string): void {
    const base = this.grid?.workingReadAllDTO || this.ReadAllDTO;
    if (!base) {
      this.errorMessage = 'This screen cannot export all records. Exporting the current page instead.';
      this.scope = 'page';
      this.sourceRows = [...(this.grid?.tableData ?? [])];
      this.recomputeRows();
      return;
    }

    const total = this.totalRowCount;
    const pageSize = Math.min(total > 0 ? total : this.maxExportRows, this.maxExportRows);

    const dto: GenericListDataReadAllPaginatedRequestDTO = {
      ...JSON.parse(JSON.stringify(base)),
      pageNo: 1,
      pageSize: pageSize,
      rowNum: 0,
      totalCount: 0
    };

    const baseWhere = (dto.whereClause || '').trim();
    if (dateClause) {
      dto.whereClause = baseWhere ? `(${baseWhere}) AND ${dateClause}` : dateClause;
    }

    this.isFetching = true;
    this.rows = [];
    this.sourceRows = [];
    this.UICListService.GenericListData_ReadAllPaginated(dto).subscribe({
      next: (res) => {
        this.allRowsCache = Array.isArray(res) ? res : [];
        this.allRowsCacheKey = dateClause;
        this.sourceRows = this.allRowsCache;
        this.recomputeRows();
        this.isFetching = false;
      },
      error: () => {
        this.isFetching = false;
        this.allRowsCache = null;
        this.allRowsCacheKey = null;
        this.errorMessage = 'Could not load all records. Please try again, or export the current page.';
        this.scope = 'page';
        this.sourceRows = [...(this.grid?.tableData ?? [])];
        this.recomputeRows();
      }
    });
  }

  // ===================== DATE FILTER =====================

  /** Visible date columns, i.e. the ones a date range can be applied to. */
  get dateColumns(): ExportColumn[] {
    return this.columns.filter(col => col.isDate);
  }

  get hasDateColumns(): boolean {
    return this.dateColumns.length > 0;
  }

  get dateColumn(): ExportColumn | null {
    if (!this.dateFilterKey) { return null; }
    return this.columns.find(col => col.key === this.dateFilterKey) ?? null;
  }

  get isDateFilterActive(): boolean {
    return !!this.dateColumn && !this.dateFilterError && !!(this.dateFrom || this.dateTo);
  }

  onDateColumnChange(event: Event): void {
    this.dateFilterKey = (event.target as HTMLSelectElement).value || '';
    this.onDateFilterChange();
  }

  onDateBoundChange(bound: 'from' | 'to', event: Event): void {
    const value = (event.target as HTMLInputElement).value || '';
    if (bound === 'from') {
      this.dateFrom = value;
    } else {
      this.dateTo = value;
    }
    this.onDateFilterChange();
  }

  /** Re-applies the filter, refetching first when the server-side predicate changed. */
  onDateFilterChange(): void {
    if (this.isBuilding) { return; }

    this.dateFilterError = '';
    if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
      // yyyy-MM-dd is lexicographically ordered, so a string compare is a valid date compare.
      this.dateFilterError = '"From" date is after "To" date.';
      this.recomputeRows();
      return;
    }

    if (this.scope === 'all') {
      this.loadAllRows();
    } else {
      this.recomputeRows();
    }
  }

  clearDateFilter(): void {
    if (!this.dateFrom && !this.dateTo) { return; }
    this.dateFrom = '';
    this.dateTo = '';
    this.dateFilterError = '';
    if (this.scope === 'all') {
      this.loadAllRows();
    } else {
      this.recomputeRows();
    }
  }

  /** Applies the date range to `sourceRows`, producing the final `rows`. */
  private recomputeRows(): void {
    const col = this.dateColumn;

    if (!col || this.dateFilterError || (!this.dateFrom && !this.dateTo)) {
      this.rows = [...this.sourceRows];
      return;
    }

    const fromDay = this.dateFrom ? this.toDayNumber(this.dateFrom) : null;
    const toDay = this.dateTo ? this.toDayNumber(this.dateTo) : null;

    this.rows = this.sourceRows.filter(row => {
      const date = this.toDate(this.readValue(row, col.key));
      if (!date) { return false; }
      // Compare whole days so a datetime value is not excluded by its time component.
      const day = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
      if (fromDay !== null && day < fromDay) { return false; }
      if (toDay !== null && day > toDay) { return false; }
      return true;
    });
  }

  /** 'yyyy-MM-dd' → 20260808, for whole-day comparisons. */
  private toDayNumber(iso: string): number {
    const [y, m, d] = iso.split('-').map(Number);
    return (y || 0) * 10000 + (m || 0) * 100 + (d || 0);
  }

  private addOneDay(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    date.setDate(date.getDate() + 1);
    return formatDate(date, 'yyyy-MM-dd', 'en-US');
  }

  /**
   * SQL fragment for the chosen range, in the dialect the rest of the app uses: single
   * quotes are DOUBLED because this clause is interpolated into dynamic SQL server-side.
   *
   * Written as `>= from AND < to+1day` rather than `BETWEEN from AND to` so a datetime
   * column still matches rows recorded during the "To" day — `BETWEEN` against a
   * midnight boundary silently drops them.
   *
   * Returns '' when there is no range, no column, or the column has no whereClauseName;
   * in that last case the range is still applied client-side by recomputeRows().
   */
  private buildDateWhereClause(): string {
    const col = this.dateColumn;
    if (!col || !col.whereClauseName || this.dateFilterError) { return ''; }
    if (!this.dateFrom && !this.dateTo) { return ''; }

    const parts: string[] = [];
    if (this.dateFrom) { parts.push(`${col.whereClauseName} >= ''${this.dateFrom}''`); }
    if (this.dateTo) { parts.push(`${col.whereClauseName} < ''${this.addOneDay(this.dateTo)}''`); }

    return parts.length > 1 ? `(${parts.join(' AND ')})` : parts[0];
  }

  // ===================== COLUMN RESOLUTION =====================

  /**
   * Reads the grid's visible columns and keeps only the ones that hold data.
   *
   * `visibleColumns` is the grid's own render source, so this automatically matches
   * on-screen membership and order. Checkbox / auto-serial / action columns are separate
   * <th> elements in the grid template and are not in this array, so they are excluded
   * for free. Print and Button columns ARE in the array but render an icon, so they are
   * filtered out here.
   */
  private resolveColumns(): ExportColumn[] {
    const visible = this.grid?.visibleColumns ?? [];

    return visible
      .filter(col => !!col && !!col.colData && !col.isButton)
      .map(col => {
        const dataType = (col.dataType ?? '').toLowerCase().trim();
        return {
          header: (col.colName ?? '').toString().trim() || col.colData,
          key: col.colData,
          isDate: !!col.isDate || dataType === 'date',
          isHtml: !!col.isHtml || !!col.isBadge || dataType === 'html',
          isNumeric: dataType === 'number',
          whereClauseName: (col.whereClauseName || col.filterDataPatchName || '').toString().trim()
        } as ExportColumn;
      });
  }

  // ===================== VALUE RESOLUTION =====================

  /**
   * Case-insensitive row lookup, mirroring UicColorTableComponent.getLowerCaseValue().
   * SP result casing is not guaranteed to match the UIC propertyName exactly.
   */
  private readValue(row: any, key: string): any {
    if (row == null || !key) { return null; }
    if (Object.prototype.hasOwnProperty.call(row, key)) { return row[key]; }
    const lower = key.toLowerCase().trim();
    const match = Object.keys(row).find(k => k.toLowerCase().trim() === lower);
    return match !== undefined ? row[match] : null;
  }

  /** Strips markup from an html/badge cell so Excel gets readable text. */
  private stripHtml(value: string): string {
    if (!value || value.indexOf('<') === -1) { return value ?? ''; }
    const holder = document.createElement('div');
    holder.innerHTML = value;
    return (holder.textContent || holder.innerText || '').replace(/\s+/g, ' ').trim();
  }

  private toDate(value: any): Date | null {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'number') {
      const fromNumber = new Date(value);
      return isNaN(fromNumber.getTime()) ? null : fromNumber;
    }
    if (typeof value !== 'string' || !value.trim()) { return null; }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Only converts to a real number when the column is declared numeric, or the value
   * already arrived as a number. Deliberately conservative: strings like '0012',
   * document numbers, GSTINs and phone numbers must stay text.
   */
  private toNumber(value: any): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value !== 'string') { return null; }
    const trimmed = value.trim().replace(/,/g, '');
    if (!trimmed || !/^-?\d+(\.\d+)?$/.test(trimmed)) { return null; }
    if (/^-?0\d/.test(trimmed)) { return null; }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /** Display string for the preview table — matches what the grid renders on screen. */
  displayValue(row: any, col: ExportColumn): string {
    const raw = this.readValue(row, col.key);
    if (raw === null || raw === undefined || raw === '') { return ''; }

    if (col.isDate) {
      const date = this.toDate(raw);
      return date ? formatDate(date, 'dd/MM/yyyy', 'en-US') : String(raw);
    }
    if (col.isHtml) { return this.stripHtml(String(raw)); }
    if (typeof raw === 'boolean') { return raw ? 'Yes' : 'No'; }
    return String(raw);
  }

  /** Typed value for an Excel cell: real dates, real numbers, plain text otherwise. */
  private cellValue(row: any, col: ExportColumn): string | number | Date | null {
    const raw = this.readValue(row, col.key);
    if (raw === null || raw === undefined || raw === '') { return null; }

    if (col.isDate) {
      return this.toDate(raw) ?? String(raw);
    }
    if (col.isHtml) {
      const text = this.stripHtml(String(raw));
      return col.isNumeric ? (this.toNumber(text) ?? text) : text;
    }
    if (typeof raw === 'boolean') { return raw ? 'Yes' : 'No'; }
    if (typeof raw === 'number') { return Number.isFinite(raw) ? raw : null; }
    if (col.isNumeric) {
      return this.toNumber(raw) ?? String(raw);
    }
    return String(raw);
  }

  // ===================== EXPORT =====================

  async downloadExcel(): Promise<void> {
    if (this.isFetching || this.isBuilding) { return; }
    if (!this.columns.length || !this.rows.length) {
      this.errorMessage = 'There is nothing to export.';
      return;
    }

    this.isBuilding = true;
    this.errorMessage = '';

    try {
      const buffer = await this.buildWorkbook();
      const name = this.buildFileName();
      this.triggerDownload(buffer, name);
      this.exported.emit({ scope: this.scope, rowCount: this.rows.length, fileName: name });
      this.closeModal();
    } catch (err) {
      console.error('Excel export failed', err);
      this.errorMessage = 'Something went wrong while building the file. Please try again.';
    } finally {
      this.isBuilding = false;
    }
  }

  private async buildWorkbook(): Promise<ArrayBuffer> {
    const cols = this.columns;
    const totalColumns = cols.length + (this.includeSerial ? 1 : 0);

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'AdvERP';
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet(this.buildSheetName());

    const border: Partial<ExcelJS.Borders> = {
      top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
      left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
      bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
      right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
    };

    // ---- optional merged title row ----
    let headerRowIndex = 1;
    if (this.reportTitle.trim()) {
      const titleRow = worksheet.addRow([this.reportTitle.trim() + this.dateRangeSuffix()]);
      titleRow.height = 26;
      worksheet.mergeCells(1, 1, 1, Math.max(totalColumns, 1));
      const titleCell = titleRow.getCell(1);
      titleCell.font = { name: 'Calibri', size: 13, bold: true, color: { argb: 'FF212529' } };
      titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
      headerRowIndex = 2;
    }

    // ---- header row: UIC labels only ----
    const headers = this.includeSerial
      ? ['#', ...cols.map(c => c.header)]
      : cols.map(c => c.header);

    const headerRow = worksheet.addRow(headers);
    headerRow.height = 24;
    headerRow.eachCell((cell) => {
      cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.headerColor } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = border;
    });

    // ---- data rows ----
    const serialOffset = this.includeSerial ? 1 : 0;

    this.rows.forEach((row, index) => {
      const values = cols.map(col => this.cellValue(row, col));
      const dataRow = worksheet.addRow(this.includeSerial ? [index + 1, ...values] : values);
      const isStripe = index % 2 === 1;

      // Iterate by index rather than eachCell() so trailing blank cells still get styled.
      for (let colNumber = 1; colNumber <= totalColumns; colNumber++) {
        const cell = dataRow.getCell(colNumber);
        cell.border = border;
        cell.font = { name: 'Calibri', size: 10 };
        if (isStripe) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F9FC' } };
        }

        const col = cols[colNumber - 1 - serialOffset];

        if (!col) {
          // the serial column
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          continue;
        }

        if (col.isDate && cell.value instanceof Date) {
          cell.numFmt = 'dd/mm/yyyy';
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        } else if (typeof cell.value === 'number') {
          cell.alignment = { vertical: 'middle', horizontal: 'right' };
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
        }
      }
    });

    // ---- widths sized from header + a sample of the data ----
    const sample = this.rows.slice(0, 200);
    for (let colNumber = 1; colNumber <= totalColumns; colNumber++) {
      const column = worksheet.getColumn(colNumber);
      const col = cols[colNumber - 1 - serialOffset];
      if (!col) {
        column.width = 6;
        continue;
      }
      let widest = col.header.length;
      for (const row of sample) {
        const length = this.displayValue(row, col).length;
        if (length > widest) { widest = length; }
      }
      column.width = Math.min(Math.max(widest + 4, 12), 45);
    }

    // ---- freeze the header and switch on autofilter ----
    worksheet.views = [{ state: 'frozen', ySplit: headerRowIndex }];
    if (totalColumns > 0) {
      worksheet.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: headerRowIndex, column: totalColumns }
      };
    }

    return await workbook.xlsx.writeBuffer() as ArrayBuffer;
  }

  /**
   * Human-readable description of the applied date range, appended to the title row so an
   * exported sheet says which slice of data it contains.
   */
  dateRangeSuffix(): string {
    const col = this.dateColumn;
    if (!col || !this.isDateFilterActive) { return ''; }
    const from = this.dateFrom ? formatDate(this.dateFrom, 'dd/MM/yyyy', 'en-US') : '';
    const to = this.dateTo ? formatDate(this.dateTo, 'dd/MM/yyyy', 'en-US') : '';

    if (from && to) { return ` — ${col.header}: ${from} to ${to}`; }
    if (from) { return ` — ${col.header}: from ${from}`; }
    return ` — ${col.header}: up to ${to}`;
  }

  private buildSheetName(): string {
    const base = (this.sheetName || this.fileName || 'Sheet1').toString();
    // Excel forbids : \ / ? * [ ] in sheet names and caps them at 31 chars.
    const cleaned = base.replace(/[:\\\/\?\*\[\]]/g, ' ').trim() || 'Sheet1';
    return cleaned.substring(0, 31);
  }

  private buildFileName(): string {
    const base = (this.fileName || 'Export').replace(/[^\w\s\-]/g, '').trim().replace(/\s+/g, '_');
    const stamp = formatDate(new Date(), 'yyyy-MM-dd', 'en-US');
    return `${base || 'Export'}_${stamp}.xlsx`;
  }

  private triggerDownload(buffer: ArrayBuffer, fileName: string): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }
}
